import { useEffect, useState } from "react";
import { ShoppingBag, Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useStore } from "@/context/StoreContext";
import { scrollToId } from "@/components/Hero";

const LINKS = [
  { label: "Flavors", href: "#flavors", testid: "nav-link-flavors" },
  { label: "Live Events", href: "#packages", testid: "nav-link-packages" },
  { label: "Gallery", href: "#gallery", testid: "nav-link-gallery" },
  { label: "Reviews", href: "#reviews", testid: "nav-link-reviews" },
  { label: "Rewards", href: "#loyalty", testid: "nav-link-loyalty" },
];

export const Navbar = () => {
  const { cartCount, setCartOpen } = useStore();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setMobileOpen(false);
    scrollToId(href);
  };

  return (
    <header data-testid="site-navbar" className={`fixed top-0 left-0 right-0 z-40 transition-[background-color,border-color,backdrop-filter] duration-500 ${scrolled ? "glass border-b border-white/10" : "bg-transparent border-b border-transparent"}`}>
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <button data-testid="nav-logo" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="flex items-center gap-3 group">
          <Logo size={38} />
          <span className="font-display font-black text-xl tracking-tight text-white">
            Cosmic<span className="text-gradient-candy">Cloudz</span>
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {LINKS.map((l) => (
            <button key={l.href} data-testid={l.testid} onClick={() => go(l.href)}
              className="text-sm font-medium text-slate-300 hover:text-cosmic-cyan transition-colors duration-300">
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button data-testid="nav-book-event" onClick={() => go("#packages")}
            className="hidden sm:inline-flex items-center rounded-full bg-gradient-to-r from-cosmic-pink to-cosmic-purple px-5 py-2.5 text-sm font-bold text-white transition-transform duration-300 hover:scale-105 glow-pink">
            Book Event
          </button>
          <button data-testid="cart-drawer-toggle" onClick={() => setCartOpen(true)} aria-label="Open cart"
            className="relative rounded-full glass p-3 text-white transition-transform duration-300 hover:scale-110">
            <ShoppingBag size={19} />
            {cartCount > 0 && (
              <span data-testid="cart-count-badge" className="absolute -top-1.5 -right-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-cosmic-pink px-1 text-[11px] font-bold text-white">
                {cartCount}
              </span>
            )}
          </button>
          <button data-testid="mobile-menu-toggle" onClick={() => setMobileOpen((o) => !o)} aria-label="Menu"
            className="md:hidden rounded-full glass p-3 text-white">
            {mobileOpen ? <X size={19} /> : <Menu size={19} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div data-testid="mobile-menu" className="md:hidden glass border-t border-white/10 px-6 py-4 flex flex-col gap-4">
          {LINKS.map((l) => (
            <button key={l.href} data-testid={`${l.testid}-mobile`} onClick={() => go(l.href)} className="text-left text-base font-medium text-slate-200">
              {l.label}
            </button>
          ))}
          <button data-testid="nav-book-event-mobile" onClick={() => go("#packages")} className="text-left text-base font-bold text-cosmic-pink">
            Book Event
          </button>
        </div>
      )}
    </header>
  );
};

export default Navbar;
